/**
 * Reusable pagination control.
 *
 * Renders a sliding window of page numbers plus first/last markers with
 * ellipses when there's a gap. Users can always see the total range and jump
 * to the ends in one click.
 *
 * Layout examples (totalPages = 20):
 *   page 0   → [<] [1] [2] [3] [4] [5] ... [20] [>]
 *   page 9   → [<] [1] ... [8] [9] [10] [11] [12] ... [20] [>]
 *   page 19  → [<] [1] ... [16] [17] [18] [19] [20] [>]
 *
 * If totalPages ≤ 7, every page is shown (no ellipses).
 *
 * Page numbers are 0-indexed internally (matches existing call sites) but
 * displayed 1-indexed.
 */
import { ReactElement } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  /** Current page (0-indexed). */
  page: number;
  /** Total number of pages. */
  totalPages: number;
  /** Called with the new 0-indexed page number. */
  onPageChange: (page: number) => void;
  /** Explicit override for the "next" button disabled state (default: page >= totalPages - 1). */
  hasMore?: boolean;
  /** Style variant. `light` = light background (default); `dark` = dark background (slate cards). */
  variant?: 'light' | 'dark';
  /** Extra classes on the wrapper. */
  className?: string;
}

function buildWindow(page: number, totalPages: number): Array<number | 'gap-left' | 'gap-right'> {
  // Small case: show every page.
  if (totalPages <= 9) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const first = 0;
  const last = totalPages - 1;
  const items: Array<number | 'gap-left' | 'gap-right'> = [first];

  // Middle window: current ± 3 (7 numbers around the current page).
  const windowStart = Math.max(first + 1, page - 3);
  const windowEnd = Math.min(last - 1, page + 3);

  if (windowStart > first + 1) items.push('gap-left');

  for (let p = windowStart; p <= windowEnd; p++) items.push(p);

  if (windowEnd < last - 1) items.push('gap-right');

  items.push(last);
  return items;
}

export default function Pagination({
  page,
  totalPages,
  onPageChange,
  hasMore,
  variant = 'light',
  className = '',
}: PaginationProps): ReactElement | null {
  if (totalPages <= 1) return null;

  const canPrev = page > 0;
  const canNext = hasMore !== undefined ? hasMore : page < totalPages - 1;

  const items = buildWindow(page, totalPages);

  const isDark = variant === 'dark';
  const arrowCls = isDark
    ? 'p-2 rounded-lg border border-stone-700 text-stone-400 hover:bg-stone-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
    : 'p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const numCls = (active: boolean) => {
    if (isDark) {
      return active
        ? 'bg-amber-600 text-white'
        : 'border border-stone-700 text-stone-400 hover:bg-stone-700';
    }
    return active
      ? 'bg-slate-800 text-white'
      : 'border border-gray-200 text-gray-600 hover:bg-gray-50';
  };
  const ellipsisCls = isDark ? 'text-stone-500' : 'text-gray-400';

  const handleClick = (p: number) => {
    onPageChange(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`flex items-center justify-center gap-1.5 sm:gap-2 mt-8 ${className}`}>
      <button
        onClick={() => handleClick(Math.max(0, page - 1))}
        disabled={!canPrev}
        className={arrowCls}
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {items.map((item, idx) => {
        if (item === 'gap-left' || item === 'gap-right') {
          return (
            <span key={item + String(idx)} className={`px-2 text-sm ${ellipsisCls}`} aria-hidden="true">
              …
            </span>
          );
        }
        return (
          <button
            key={item}
            onClick={() => handleClick(item)}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${numCls(item === page)}`}
            aria-current={item === page ? 'page' : undefined}
          >
            {item + 1}
          </button>
        );
      })}

      <button
        onClick={() => handleClick(page + 1)}
        disabled={!canNext}
        className={arrowCls}
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
